import React, { useState, useEffect } from "react";
import {
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Switch,
  Button,
  Text,
  Box,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Divider,
  Badge,
  useToast,
} from "@chakra-ui/react";
import { FiCalendar, FiInfo, FiSave, FiX } from "react-icons/fi";

const AddHoliday = ({
  closeModal,
  editHoliday = {},
  todo = "new",
  addHolidayPayload,
  editHolidayPayload,
  addHolidayIsLoading = false,
  editHolidayIsLoading = false,
  refetchGetHolidays,
}) => {
  const [formData, setFormData] = useState({
    holiday_name: "",
    holiday_date: "",
    holiday_description: "",
    is_recurring: false,
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();

  // Initialize form data when editing
  useEffect(() => {
    if (todo === "edit" && editHoliday) {
      setFormData({
        holiday_name: editHoliday.name || "",
        holiday_date: editHoliday.date ? editHoliday.date.split('T')[0] : "",
        holiday_description: editHoliday.description || "",
        is_recurring: editHoliday.is_recurring || false,
        holiday_id: editHoliday.holiday_id,
      });
    }
  }, [todo, editHoliday]);

  const validateForm = () => {
    const newErrors = {};

    // Validate holiday name
    if (!formData.holiday_name.trim()) {
      newErrors.holiday_name = "Holiday name is required";
    } else if (formData.holiday_name.trim().length < 2) {
      newErrors.holiday_name = "Holiday name must be at least 2 characters";
    }

    // Validate holiday date
    if (!formData.holiday_date) {
      newErrors.holiday_date = "Holiday date is required";
    } else {
      const selectedDate = new Date(formData.holiday_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (selectedDate < today) {
        newErrors.holiday_date = "Holiday date cannot be in the past";
      }
    }

    // Validate description (optional but if provided, check length)
    if (formData.holiday_description && formData.holiday_description.length > 500) {
      newErrors.holiday_description = "Description must be less than 500 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fix the errors in the form",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      if (todo === "edit") {
        await editHolidayPayload(formData);
      } else {
        await addHolidayPayload(formData);
      }

      // Reset form
      setFormData({
        holiday_name: "",
        holiday_date: "",
        holiday_description: "",
        is_recurring: false,
      });
      setErrors({});

      // Close modal and refresh data
      closeModal();
      if (refetchGetHolidays) {
        refetchGetHolidays();
      }

      toast({
        title: todo === "edit" ? "Holiday Updated" : "Holiday Created",
        description: todo === "edit" 
          ? "Holiday has been updated successfully" 
          : "Holiday has been created successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Something went wrong",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    closeModal();
  };

  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const isLoading = addHolidayIsLoading || editHolidayIsLoading || isSubmitting;

  return (
    <Box as="form" onSubmit={handleSubmit}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Box>
          <Text fontSize="lg" fontWeight="600" color="gray.900">
            {todo === "edit" ? "Edit Holiday" : "Add New Holiday"}
          </Text>
          <Text fontSize="sm" color="gray.600" mt={1}>
            {todo === "edit" 
              ? "Update the holiday information below" 
              : "Fill in the details to create a new holiday"
            }
          </Text>
        </Box>

        {/* Holiday Name */}
        <FormControl isInvalid={!!errors.holiday_name}>
          <FormLabel fontWeight="500">Holiday Name *</FormLabel>
          <Input
            placeholder="e.g., Christmas Day, New Year's Day"
            value={formData.holiday_name}
            onChange={(e) => handleInputChange("holiday_name", e.target.value)}
            size="lg"
            borderRadius="md"
          />
          {errors.holiday_name && (
            <Text color="red.500" fontSize="sm" mt={1}>
              {errors.holiday_name}
            </Text>
          )}
        </FormControl>

        {/* Holiday Date */}
        <FormControl isInvalid={!!errors.holiday_date}>
          <FormLabel fontWeight="500">Holiday Date *</FormLabel>
          <Input
            type="date"
            value={formData.holiday_date}
            onChange={(e) => handleInputChange("holiday_date", e.target.value)}
            min={getMinDate()}
            size="lg"
            borderRadius="md"
          />
          {formData.holiday_date && (
            <Text fontSize="sm" color="blue.600" mt={1}>
              <FiCalendar style={{ display: 'inline', marginRight: '4px' }} />
              {formatDateForDisplay(formData.holiday_date)}
            </Text>
          )}
          {errors.holiday_date && (
            <Text color="red.500" fontSize="sm" mt={1}>
              {errors.holiday_date}
            </Text>
          )}
        </FormControl>

        {/* Description */}
        <FormControl isInvalid={!!errors.holiday_description}>
          <FormLabel fontWeight="500">Description</FormLabel>
          <Textarea
            placeholder="Optional description of the holiday..."
            value={formData.holiday_description}
            onChange={(e) => handleInputChange("holiday_description", e.target.value)}
            rows={3}
            resize="vertical"
            borderRadius="md"
          />
          <Text fontSize="xs" color="gray.500" mt={1}>
            {formData.holiday_description.length}/500 characters
          </Text>
          {errors.holiday_description && (
            <Text color="red.500" fontSize="sm" mt={1}>
              {errors.holiday_description}
            </Text>
          )}
        </FormControl>

        {/* Recurring Switch */}
        <FormControl>
          <HStack justify="space-between" align="center">
            <Box>
              <FormLabel fontWeight="500" mb={1}>Recurring Holiday</FormLabel>
              <Text fontSize="sm" color="gray.600">
                This holiday repeats every year
              </Text>
            </Box>
            <Switch
              isChecked={formData.is_recurring}
              onChange={(e) => handleInputChange("is_recurring", e.target.checked)}
              colorScheme="blue"
              size="lg"
            />
          </HStack>
        </FormControl>

        {/* Info Alert */}
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle>Holiday Information</AlertTitle>
            <AlertDescription>
              {formData.is_recurring 
                ? "This holiday will automatically appear every year on the same date."
                : "This is a one-time holiday for the specified date only."
              }
            </AlertDescription>
          </Box>
        </Alert>

        {/* Preview */}
        {formData.holiday_name && formData.holiday_date && (
          <Box
            bg="gray.50"
            p={4}
            borderRadius="md"
            border="1px solid"
            borderColor="gray.200"
          >
            <Text fontSize="sm" fontWeight="500" color="gray.700" mb={2}>
              Preview:
            </Text>
            <HStack spacing={3} align="center">
              <Badge
                colorScheme={formData.is_recurring ? "green" : "blue"}
                variant="subtle"
                borderRadius="full"
                px={3}
              >
                {formData.is_recurring ? "Recurring" : "One-time"}
              </Badge>
              <Text fontWeight="500">{formData.holiday_name}</Text>
              <Text color="gray.600">•</Text>
              <Text color="gray.600">{formatDateForDisplay(formData.holiday_date)}</Text>
            </HStack>
            {formData.holiday_description && (
              <Text fontSize="sm" color="gray.600" mt={2}>
                {formData.holiday_description}
              </Text>
            )}
          </Box>
        )}

        <Divider />

        {/* Action Buttons */}
        <HStack spacing={3} justify="flex-end">
          <Button
            leftIcon={<FiX />}
            variant="outline"
            onClick={handleCancel}
            isDisabled={isLoading}
            size="lg"
          >
            Cancel
          </Button>
          <Button
            leftIcon={<FiSave />}
            colorScheme="blue"
            type="submit"
            isLoading={isLoading}
            loadingText={todo === "edit" ? "Updating..." : "Creating..."}
            size="lg"
          >
            {todo === "edit" ? "Update Holiday" : "Create Holiday"}
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
};

export default AddHoliday; 